import React, { useState, useEffect } from 'react';
import { Save, RefreshCw } from 'lucide-react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { useToast } from '../../hooks/use-toast';
import api from '../../services/api';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const FooterSettings = () => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [footerTexts, setFooterTexts] = useState({
    'footer.logo_title': 'Kuran-i Kerim',
    'footer.description': 'Yuce Kuran-i Kerimi okuyun, dinleyin ve anlayin. Farklı mealler ve hafızlarla Kuran-i Kerime ulasin.',
    'footer.quick_links_title': 'Hızlı Erişim',
    'footer.link_home': 'Ana Sayfa',
    'footer.link_surahs': 'Sureler',
    'footer.link_reciters': 'Hafizlar',
    'footer.link_meals': 'Mealler',
    'footer.top_surahs_title': 'En Çok Dinlenen Sureler',
    'footer.loading_text': 'Yükleniyor...',
    'footer.button_read': 'Oku',
    'footer.button_listen': 'Dinle',
    'footer.all_surahs_link': 'Tüm Sureler →',
    'footer.copyright': 'Kuran-i Kerim. Tum haklari saklidir.',
    'footer.bottom_message': 'Rabbimiz, bizi dogruluktan ayirma ve bizi bagislayanlardansin.',
  });

  useEffect(() => {
    const loadFooterTexts = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/site-texts`);
        const texts = await response.json();
        
        const footerData = {};
        texts.forEach(text => {
          if (text.key.startsWith('footer.')) {
            footerData[text.key] = text.value;
          }
        });
        
        setFooterTexts(prev => ({ ...prev, ...footerData }));
      } catch (error) {
        console.error('Error loading footer texts:', error);
        toast({
          title: "Hata",
          description: "Footer ayarları yüklenemedi",
          variant: "destructive"
        });
      } finally {
        setLoading(false);
      }
    };
    
    loadFooterTexts();
  }, [toast]);

  const loadFooterTexts = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/site-texts`);
      const texts = await response.json();
      
      const footerData = {};
      texts.forEach(text => {
        if (text.key.startsWith('footer.')) {
          footerData[text.key] = text.value;
        }
      });
      
      setFooterTexts(prev => ({ ...prev, ...footerData }));
    } catch (error) {
      console.error('Error loading footer texts:', error);
      toast({
        title: "Hata",
        description: "Footer ayarları yüklenemedi",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (key, value) => {
    setFooterTexts(prev => ({ ...prev, [key]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('adminToken');
      
      // Get all existing site texts
      const response = await fetch(`${BACKEND_URL}/api/admin/site-texts`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const allTexts = await response.json();

      // Update or create footer texts
      for (const [key, value] of Object.entries(footerTexts)) {
        const existingText = allTexts.find(t => t.key === key);
        
        if (existingText) {
          // Update existing
          await fetch(`${BACKEND_URL}/api/admin/site-texts/${existingText.id}`, {
            method: 'PUT',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              key: key,
              value: value,
              description: getDescription(key),
              category: 'footer'
            })
          });
        } else {
          // Create new
          await fetch(`${BACKEND_URL}/api/admin/site-texts`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              key: key,
              value: value,
              description: getDescription(key),
              category: 'footer'
            })
          });
        }
      }

      toast({
        title: "Başarılı",
        description: "Footer ayarları kaydedildi",
        variant: "default"
      });
    } catch (error) {
      console.error('Error saving footer texts:', error);
      toast({
        title: "Hata",
        description: "Footer ayarları kaydedilemedi",
        variant: "destructive"
      });
    } finally {
      setSaving(false);
    }
  };

  const getDescription = (key) => {
    const descriptions = {
      'footer.logo_title': 'Footer - Logo başlığı',
      'footer.description': 'Footer - Açıklama metni',
      'footer.quick_links_title': 'Footer - Hızlı erişim başlığı',
      'footer.link_home': 'Footer - Ana sayfa linki',
      'footer.link_surahs': 'Footer - Sureler linki',
      'footer.link_reciters': 'Footer - Hafızlar linki',
      'footer.link_meals': 'Footer - Mealler linki',
      'footer.top_surahs_title': 'Footer - En çok dinlenen sureler başlığı',
      'footer.loading_text': 'Footer - Yükleniyor mesajı',
      'footer.button_read': 'Footer - Oku butonu',
      'footer.button_listen': 'Footer - Dinle butonu',
      'footer.all_surahs_link': 'Footer - Tüm sureler linki',
      'footer.copyright': 'Footer - Copyright metni',
      'footer.bottom_message': 'Footer - Alt mesaj',
    };
    return descriptions[key] || '';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-[#8B7355]">Yükleniyor...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-[#5C4033]">Footer Ayarları</h2>
          <p className="text-[#8B7355] mt-1">Footer&apos;da görünen tüm metinleri buradan düzenleyebilirsiniz</p>
        </div>
        <div className="flex gap-3">
          <Button
            onClick={loadFooterTexts}
            variant="outline"
            className="border-[#8B7355] text-[#8B7355] hover:bg-[#F9F6F1]"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Yenile
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-[#8B7355] hover:bg-[#6B5744]"
          >
            <Save className="h-4 w-4 mr-2" />
            {saving ? 'Kaydediliyor...' : 'Kaydet'}
          </Button>
        </div>
      </div>

      {/* Logo Section */}
      <Card className="bg-white border-[#E8DCC8] p-6">
        <h3 className="text-lg font-bold text-[#5C4033] mb-4">Logo ve Açıklama</h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-[#5C4033] mb-2">
              Logo Başlığı
            </label>
            <Input
              value={footerTexts['footer.logo_title']}
              onChange={(e) => handleChange('footer.logo_title', e.target.value)}
              className="border-[#E8DCC8] focus:border-[#8B7355]"
              placeholder="Kuran-i Kerim"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-[#5C4033] mb-2">
              Açıklama Metni
            </label>
            <Textarea
              value={footerTexts['footer.description']}
              onChange={(e) => handleChange('footer.description', e.target.value)}
              className="border-[#E8DCC8] focus:border-[#8B7355]"
              rows={3}
              placeholder="Yuce Kuran-i Kerimi okuyun, dinleyin ve anlayin..."
            />
          </div>
        </div>
      </Card>

      {/* Quick Links Section */}
      <Card className="bg-white border-[#E8DCC8] p-6">
        <h3 className="text-lg font-bold text-[#5C4033] mb-4">Hızlı Erişim</h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-[#5C4033] mb-2">
              Bölüm Başlığı
            </label>
            <Input
              value={footerTexts['footer.quick_links_title']}
              onChange={(e) => handleChange('footer.quick_links_title', e.target.value)}
              className="border-[#E8DCC8] focus:border-[#8B7355]"
              placeholder="Hızlı Erişim"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-[#5C4033] mb-2">
                Ana Sayfa Linki
              </label>
              <Input
                value={footerTexts['footer.link_home']}
                onChange={(e) => handleChange('footer.link_home', e.target.value)}
                className="border-[#E8DCC8] focus:border-[#8B7355]"
                placeholder="Ana Sayfa"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#5C4033] mb-2">
                Sureler Linki
              </label>
              <Input
                value={footerTexts['footer.link_surahs']}
                onChange={(e) => handleChange('footer.link_surahs', e.target.value)}
                className="border-[#E8DCC8] focus:border-[#8B7355]"
                placeholder="Sureler"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#5C4033] mb-2">
                Hafızlar Linki
              </label>
              <Input
                value={footerTexts['footer.link_reciters']}
                onChange={(e) => handleChange('footer.link_reciters', e.target.value)}
                className="border-[#E8DCC8] focus:border-[#8B7355]"
                placeholder="Hafizlar"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#5C4033] mb-2">
                Mealler Linki
              </label>
              <Input
                value={footerTexts['footer.link_meals']}
                onChange={(e) => handleChange('footer.link_meals', e.target.value)}
                className="border-[#E8DCC8] focus:border-[#8B7355]"
                placeholder="Mealler"
              />
            </div>
          </div>
        </div>
      </Card>

      {/* Top Surahs Section */}
      <Card className="bg-white border-[#E8DCC8] p-6">
        <h3 className="text-lg font-bold text-[#5C4033] mb-4">En Çok Dinlenen Sureler</h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-[#5C4033] mb-2">
              Bölüm Başlığı
            </label>
            <Input
              value={footerTexts['footer.top_surahs_title']}
              onChange={(e) => handleChange('footer.top_surahs_title', e.target.value)}
              className="border-[#E8DCC8] focus:border-[#8B7355]"
              placeholder="En Çok Dinlenen Sureler"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-[#5C4033] mb-2">
                Yükleniyor Mesajı
              </label>
              <Input
                value={footerTexts['footer.loading_text']}
                onChange={(e) => handleChange('footer.loading_text', e.target.value)}
                className="border-[#E8DCC8] focus:border-[#8B7355]"
                placeholder="Yükleniyor..."
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#5C4033] mb-2">
                Tüm Sureler Linki
              </label>
              <Input
                value={footerTexts['footer.all_surahs_link']}
                onChange={(e) => handleChange('footer.all_surahs_link', e.target.value)}
                className="border-[#E8DCC8] focus:border-[#8B7355]"
                placeholder="Tüm Sureler →"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-[#5C4033] mb-2">
                Oku Butonu
              </label>
              <Input
                value={footerTexts['footer.button_read']}
                onChange={(e) => handleChange('footer.button_read', e.target.value)}
                className="border-[#E8DCC8] focus:border-[#8B7355]"
                placeholder="Oku"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#5C4033] mb-2">
                Dinle Butonu
              </label>
              <Input
                value={footerTexts['footer.button_listen']}
                onChange={(e) => handleChange('footer.button_listen', e.target.value)}
                className="border-[#E8DCC8] focus:border-[#8B7355]"
                placeholder="Dinle"
              />
            </div>
          </div>
        </div>
      </Card>

      {/* Bottom Section */}
      <Card className="bg-white border-[#E8DCC8] p-6">
        <h3 className="text-lg font-bold text-[#5C4033] mb-4">Alt Bölüm</h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-[#5C4033] mb-2">
              Copyright Metni
            </label>
            <Input
              value={footerTexts['footer.copyright']}
              onChange={(e) => handleChange('footer.copyright', e.target.value)}
              className="border-[#E8DCC8] focus:border-[#8B7355]"
              placeholder="Kuran-i Kerim. Tum haklari saklidir."
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-[#5C4033] mb-2">
              Alt Mesaj
            </label>
            <Input
              value={footerTexts['footer.bottom_message']}
              onChange={(e) => handleChange('footer.bottom_message', e.target.value)}
              className="border-[#E8DCC8] focus:border-[#8B7355]"
              placeholder="Rabbimiz, bizi dogruluktan ayirma ve bizi bagislayanlardansin."
            />
          </div>
        </div>
      </Card>

      {/* Save Button at Bottom */}
      <div className="flex justify-end">
        <Button
          onClick={handleSave}
          disabled={saving}
          className="bg-[#8B7355] hover:bg-[#6B5744]"
          size="lg"
        >
          <Save className="h-5 w-5 mr-2" />
          {saving ? 'Kaydediliyor...' : 'Tüm Değişiklikleri Kaydet'}
        </Button>
      </div>
    </div>
  );
};

export default FooterSettings;